/**
 * HTML to Markdown Converter
 *
 * Converts HTML content to well-structured Markdown.
 * Designed for extracting readable page content for AI context.
 */

/**
 * Configuration for the HTML to Markdown converter
 */
export interface HtmlToMarkdownOptions {
  /** Include link URLs in output (default: true) */
  includeLinkUrls?: boolean;
  /** Include image URLs in output (default: true) */
  includeImageUrls?: boolean;
  /** Maximum heading level to preserve (default: 6) */
  maxHeadingLevel?: number;
  /** Preserve line breaks within paragraphs (default: false) */
  preserveLineBreaks?: boolean;
}

const DEFAULT_OPTIONS: Required<HtmlToMarkdownOptions> = {
  includeLinkUrls: true,
  includeImageUrls: true,
  maxHeadingLevel: 6,
  preserveLineBreaks: false,
};

/**
 * Elements that should be completely ignored (their content too)
 */
const IGNORED_ELEMENTS = new Set([
  "script",
  "style",
  "noscript",
  "template",
  "svg",
  "canvas",
  "audio",
  "video",
  "iframe",
  "object",
  "embed",
]);

/**
 * Block-level elements that need line breaks around them
 */
const BLOCK_ELEMENTS = new Set([
  "p",
  "div",
  "section",
  "article",
  "header",
  "footer",
  "main",
  "aside",
  "nav",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "blockquote",
  "pre",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "figure",
  "figcaption",
  "hr",
  "br",
  "address",
  "form",
  "fieldset",
]);

/**
 * Convert an HTML string to Markdown
 */
export function htmlToMarkdown(
  html: string,
  options: HtmlToMarkdownOptions = {}
): string {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Create a temporary DOM element to parse the HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Convert the document body
  const markdown = processNode(doc.body, opts, {
    listDepth: 0,
    ordered: false,
  });

  // Clean up the output
  return cleanMarkdown(markdown);
}

/**
 * Context for processing nested elements
 */
interface ProcessContext {
  listDepth: number;
  ordered: boolean;
  listIndex?: number;
  inCodeBlock?: boolean;
  inTable?: boolean;
}

/**
 * Process a DOM node and its children recursively
 */
function processNode(
  node: Node,
  options: Required<HtmlToMarkdownOptions>,
  context: ProcessContext
): string {
  // Handle text nodes
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || "";
    if (context.inCodeBlock) {
      return text;
    }
    // Normalize whitespace for non-code content
    return normalizeWhitespace(text);
  }

  // Handle element nodes
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  const element = node as Element;
  const tagName = element.tagName.toLowerCase();

  // Skip ignored elements entirely
  if (IGNORED_ELEMENTS.has(tagName)) {
    return "";
  }

  // Skip hidden elements
  if (
    element.hasAttribute("hidden") ||
    element.getAttribute("aria-hidden") === "true" ||
    (element as HTMLElement).style?.display === "none" ||
    (element as HTMLElement).style?.visibility === "hidden"
  ) {
    return "";
  }

  // Process based on element type
  switch (tagName) {
    // Headings
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6": {
      const level = Math.min(
        parseInt(tagName.charAt(1), 10),
        options.maxHeadingLevel
      );
      const prefix = "#".repeat(level);
      const content = processChildren(element, options, context).trim();
      return content ? `\n\n${prefix} ${content}\n\n` : "";
    }

    // Paragraphs
    case "p": {
      const content = processChildren(element, options, context).trim();
      return content ? `\n\n${content}\n\n` : "";
    }

    // Line breaks
    case "br":
      return options.preserveLineBreaks ? "\n" : " ";

    // Horizontal rule
    case "hr":
      return "\n\n---\n\n";

    // Bold/Strong
    case "strong":
    case "b": {
      const content = processChildren(element, options, context).trim();
      return content ? `**${content}**` : "";
    }

    // Italic/Emphasis
    case "em":
    case "i": {
      const content = processChildren(element, options, context).trim();
      return content ? `*${content}*` : "";
    }

    // Strikethrough
    case "s":
    case "strike":
    case "del": {
      const content = processChildren(element, options, context).trim();
      return content ? `~~${content}~~` : "";
    }

    // Inline code
    case "code": {
      if (context.inCodeBlock) {
        return processChildren(element, options, context);
      }
      const content = element.textContent || "";
      return content ? `\`${content}\`` : "";
    }

    // Code blocks
    case "pre": {
      const codeElement = element.querySelector("code");
      const content = (codeElement || element).textContent || "";
      const language =
        codeElement?.className.match(/language-(\w+)/)?.[1] || "";
      return content.trim()
        ? `\n\n\`\`\`${language}\n${content.trim()}\n\`\`\`\n\n`
        : "";
    }

    // Blockquotes
    case "blockquote": {
      const content = processChildren(element, options, context).trim();
      if (!content) return "";
      const lines = content.split("\n").map((line) => `> ${line}`);
      return `\n\n${lines.join("\n")}\n\n`;
    }

    // Links
    case "a": {
      const content = processChildren(element, options, context).trim();
      const href = element.getAttribute("href") || "";
      if (!content) return "";
      if (options.includeLinkUrls && href && !href.startsWith("#")) {
        // Convert relative URLs to absolute
        const absoluteUrl = new URL(href, window.location.href).href;
        return `[${content}](${absoluteUrl})`;
      }
      return content;
    }

    // Images
    case "img": {
      const alt = element.getAttribute("alt") || "image";
      const src = element.getAttribute("src") || "";
      if (options.includeImageUrls && src) {
        const absoluteUrl = new URL(src, window.location.href).href;
        return `![${alt}](${absoluteUrl})`;
      }
      return alt ? `[Image: ${alt}]` : "";
    }

    // Unordered lists
    case "ul": {
      const content = processChildren(element, options, {
        ...context,
        listDepth: context.listDepth + 1,
        ordered: false,
      });
      return context.listDepth === 0 ? `\n\n${content.trim()}\n\n` : content;
    }

    // Ordered lists
    case "ol": {
      const content = processChildren(element, options, {
        ...context,
        listDepth: context.listDepth + 1,
        ordered: true,
        listIndex: 1,
      });
      return context.listDepth === 0 ? `\n\n${content.trim()}\n\n` : content;
    }

    // List items
    case "li": {
      const indent = "  ".repeat(Math.max(0, context.listDepth - 1));
      const bullet = context.ordered ? `${context.listIndex || 1}.` : "-";
      const content = processChildren(element, options, context).trim();
      return content ? `${indent}${bullet} ${content}\n` : "";
    }

    // Tables
    case "table": {
      return processTable(element, options);
    }

    // Skip table internal elements (handled by processTable)
    case "thead":
    case "tbody":
    case "tfoot":
    case "tr":
    case "th":
    case "td":
      return processChildren(element, options, context);

    // Definition lists
    case "dl": {
      const content = processChildren(element, options, context).trim();
      return content ? `\n\n${content}\n\n` : "";
    }
    case "dt": {
      const content = processChildren(element, options, context).trim();
      return content ? `**${content}**\n` : "";
    }
    case "dd": {
      const content = processChildren(element, options, context).trim();
      return content ? `: ${content}\n` : "";
    }

    // Figure and caption
    case "figure": {
      const content = processChildren(element, options, context).trim();
      return content ? `\n\n${content}\n\n` : "";
    }
    case "figcaption": {
      const content = processChildren(element, options, context).trim();
      return content ? `\n*${content}*\n` : "";
    }

    // Mark/highlight
    case "mark": {
      const content = processChildren(element, options, context).trim();
      return content ? `==${content}==` : "";
    }

    // Abbreviation
    case "abbr": {
      const content = processChildren(element, options, context).trim();
      const title = element.getAttribute("title");
      return title ? `${content} (${title})` : content;
    }

    // Subscript and superscript
    case "sub": {
      const content = processChildren(element, options, context).trim();
      return content ? `~${content}~` : "";
    }
    case "sup": {
      const content = processChildren(element, options, context).trim();
      return content ? `^${content}^` : "";
    }

    // Time element
    case "time": {
      const datetime = element.getAttribute("datetime");
      const content = processChildren(element, options, context).trim();
      return datetime && datetime !== content
        ? `${content} (${datetime})`
        : content;
    }

    // Default: process children with block handling
    default: {
      const content = processChildren(element, options, context);
      // Add spacing for block elements
      if (BLOCK_ELEMENTS.has(tagName)) {
        return `\n${content}\n`;
      }
      return content;
    }
  }
}

/**
 * Process all child nodes of an element
 */
function processChildren(
  element: Element,
  options: Required<HtmlToMarkdownOptions>,
  context: ProcessContext
): string {
  let result = "";
  let listIndex = context.listIndex || 1;

  for (const child of Array.from(element.childNodes)) {
    // Update list index for ordered lists
    if (
      context.ordered &&
      child.nodeType === Node.ELEMENT_NODE &&
      (child as Element).tagName.toLowerCase() === "li"
    ) {
      result += processNode(child, options, { ...context, listIndex });
      listIndex++;
    } else {
      result += processNode(child, options, context);
    }
  }

  return result;
}

/**
 * Process a table element to Markdown
 */
function processTable(
  table: Element,
  options: Required<HtmlToMarkdownOptions>
): string {
  const rows: string[][] = [];
  const headerRows: string[][] = [];

  // Process header rows
  const thead = table.querySelector("thead");
  if (thead) {
    for (const tr of Array.from(thead.querySelectorAll("tr"))) {
      const cells = Array.from(tr.querySelectorAll("th, td")).map((cell) =>
        processNode(cell, options, {
          listDepth: 0,
          ordered: false,
          inTable: true,
        })
          .trim()
          .replace(/\|/g, "\\|")
          .replace(/\n/g, " ")
      );
      if (cells.length > 0) {
        headerRows.push(cells);
      }
    }
  }

  // Process body rows
  const tbody = table.querySelector("tbody") || table;
  for (const tr of Array.from(tbody.querySelectorAll("tr"))) {
    // Skip if this is in thead
    if (tr.closest("thead")) continue;

    const cells = Array.from(tr.querySelectorAll("th, td")).map((cell) =>
      processNode(cell, options, {
        listDepth: 0,
        ordered: false,
        inTable: true,
      })
        .trim()
        .replace(/\|/g, "\\|")
        .replace(/\n/g, " ")
    );
    if (cells.length > 0) {
      rows.push(cells);
    }
  }

  // If no header rows, use first body row as header
  if (headerRows.length === 0 && rows.length > 0) {
    headerRows.push(rows.shift()!);
  }

  if (headerRows.length === 0) {
    return "";
  }

  // Determine column count
  const colCount = Math.max(
    ...headerRows.map((r) => r.length),
    ...rows.map((r) => r.length)
  );

  // Build markdown table
  const lines: string[] = [];

  // Header
  for (const headerRow of headerRows) {
    const paddedRow = [...headerRow];
    while (paddedRow.length < colCount) paddedRow.push("");
    lines.push(`| ${paddedRow.join(" | ")} |`);
  }

  // Separator
  lines.push(`| ${Array(colCount).fill("---").join(" | ")} |`);

  // Body rows
  for (const row of rows) {
    const paddedRow = [...row];
    while (paddedRow.length < colCount) paddedRow.push("");
    lines.push(`| ${paddedRow.join(" | ")} |`);
  }

  return `\n\n${lines.join("\n")}\n\n`;
}

/**
 * Normalize whitespace in text content
 */
function normalizeWhitespace(text: string): string {
  // Replace multiple whitespace with single space
  return text.replace(/[\s\n\r]+/g, " ");
}

/**
 * Clean up the final markdown output
 */
function cleanMarkdown(markdown: string): string {
  return (
    markdown
      // Remove leading/trailing whitespace on each line
      .split("\n")
      .map((line) => line.trimEnd())
      .join("\n")
      // Remove more than 2 consecutive newlines (do this after joining)
      .replace(/\n{3,}/g, "\n\n")
      // Remove leading/trailing newlines
      .trim()
  );
}
